import React, { useState, useEffect } from 'react';
import './SaleSettingsModal.css';

const SaleSettingsModal = ({ product, onClose, onSave }) => {
  const [isOnSale, setIsOnSale] = useState(product?.isOnSale || false);
  const [salePrice, setSalePrice] = useState(product?.price || '');
  const [originalPrice, setOriginalPrice] = useState(product?.originalPrice || product?.price || '');
  const [saleStartDate, setSaleStartDate] = useState('');
  const [saleEndDate, setSaleEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (product) {
      setIsOnSale(product.isOnSale || false);
      setSalePrice(product.price || '');
      setOriginalPrice(product.originalPrice || product.price || '');
      
      // Set dates if they exist
      if (product.saleStartDate) {
        const startDate = new Date(product.saleStartDate);
        setSaleStartDate(startDate.toISOString().split('T')[0]);
      }
      if (product.saleEndDate) {
        const endDate = new Date(product.saleEndDate);
        setSaleEndDate(endDate.toISOString().split('T')[0]);
      }
    }
  }, [product]);

  const handleSave = async () => {
    setError(null);
    
    // Validation
    if (isOnSale) {
      if (!salePrice || salePrice <= 0) {
        setError('Please enter a valid sale price');
        return;
      }
      if (saleStartDate && saleEndDate && new Date(saleStartDate) > new Date(saleEndDate)) {
        setError('End date must be after start date');
        return;
      }
    }

    try {
      setSaving(true);
      
      const saleData = {
        isOnSale: isOnSale,
        price: isOnSale ? parseFloat(salePrice) : originalPrice,
        originalPrice: isOnSale && originalPrice ? parseFloat(originalPrice) : undefined,
        saleStartDate: saleStartDate || undefined,
        saleEndDate: saleEndDate || undefined
      };

      await onSave(product.id || product._id, saleData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save sale settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSale = (checked) => {
    setIsOnSale(checked);
    if (!checked) {
      // If turning off sale, reset to original price
      setSalePrice(originalPrice);
    }
  };

  return (
    <div className="sale-settings-modal-overlay" onClick={onClose}>
      <div className="sale-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sale-settings-modal-header">
          <h2>Sale Settings</h2>
          <button className="sale-settings-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="sale-settings-modal-body">
          {product && (
            <div className="sale-settings-product-info">
              <h3>{product.name}</h3>
              <p className="sale-settings-product-sku">SKU: {product.sku || 'N/A'}</p>
            </div>
          )}

          {error && (
            <div className="sale-settings-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <div className="sale-settings-form">
            {/* Sale Toggle */}
            <div className="sale-settings-field">
              <label className="sale-settings-toggle-label">
                <input
                  type="checkbox"
                  checked={isOnSale}
                  onChange={(e) => handleToggleSale(e.target.checked)}
                  className="sale-settings-toggle-input"
                />
                <span className="sale-settings-toggle-slider"></span>
                <span className="sale-settings-toggle-text">Enable Sale</span>
              </label>
            </div>

            {/* Original Price */}
            <div className="sale-settings-field">
              <label>Original Price (PKR)</label>
              <input
                type="number"
                value={originalPrice}
                onChange={(e) => setOriginalPrice(e.target.value)}
                placeholder="Enter original price"
                min="0"
                step="0.01"
                disabled={!isOnSale}
                className="sale-settings-input"
              />
            </div>

            {/* Sale Price */}
            {isOnSale && (
              <>
                <div className="sale-settings-field">
                  <label>Sale Price (PKR) *</label>
                  <input
                    type="number"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    placeholder="Enter sale price"
                    min="0"
                    step="0.01"
                    className="sale-settings-input"
                    required
                  />
                </div>

                {/* Sale Dates */}
                <div className="sale-settings-dates">
                  <div className="sale-settings-field">
                    <label>Sale Start Date</label>
                    <input
                      type="date"
                      value={saleStartDate}
                      onChange={(e) => setSaleStartDate(e.target.value)}
                      className="sale-settings-input"
                    />
                  </div>

                  <div className="sale-settings-field">
                    <label>Sale End Date</label>
                    <input
                      type="date"
                      value={saleEndDate}
                      onChange={(e) => setSaleEndDate(e.target.value)}
                      className="sale-settings-input"
                      min={saleStartDate || undefined}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="sale-settings-modal-footer">
          <button
            className="sale-settings-btn sale-settings-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="sale-settings-btn sale-settings-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleSettingsModal;

